import React from "react";
import {
    Box, Checkbox, FormControlLabel, FormGroup, ListItemText,
    MenuItem, Select, SelectChangeEvent, Typography
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { selectChartConfig, selectChartScalesAndLinesSettings } from "../../selectors/chart";
import { updateChartConfig } from "../../reducer/chart.reducer";
import { ScalesAndLinesSettings as ScalesAndLinesSettingsType } from "../chartV2/chart.typings";
import { chartSettingsFormGroupSx, chartSettingsSectionDividerSx, chartSettingsSectionTitleSx } from "./chartSettingsLayout";
import { isTransformedChartType } from "../../util/chart";
import { ChartType } from "../../proto/generated/chart";

type Props = {
    viewKey: string;
};

const checkboxSx = {
    ml: 0,
    "& .MuiFormControlLabel-label": { fontSize: "14px" }
};

const HIGH_LOW_OPTIONS = [
    { value: "showHighLowLine", label: "Line" },
    { value: "showHighLowLabel", label: "Value" },
];

const INDICATOR_OPTIONS = [
    { value: "showIndicatorLabel", label: "Name" },
    { value: "showIndicatorValue", label: "Value" },
];

const SYMBOL_OPTIONS = [
    { value: "showSymbolLine", label: "Line" },
    { value: "showSymbolValue", label: "Value" },
];

const PREV_DAY_CLOSE_OPTIONS = [
    { value: "showPrevDayCloseLine", label: "Line" },
    { value: "showPrevDayCloseLabel", label: "Value" },
];

const ScalesAndLinesSettings = ({ viewKey }: Props) => {
    const dispatch = useDispatch();
    const scalesAndLinesSettings = useSelector(selectChartScalesAndLinesSettings(viewKey));
    const chartType = useSelector(selectChartConfig(viewKey))?.chartType ?? ChartType.CANDLESTICK;
    const showCountdownSetting = !isTransformedChartType(chartType);

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, field: keyof ScalesAndLinesSettingsType) => {
        dispatch(updateChartConfig({
            viewKey,
            config: {
                scalesAndLinesSettings: {
                    ...scalesAndLinesSettings,
                    [field]: event.target.checked
                }
            },
            createIfAbsent: false
        }));
    };

    const showHighLowLine = scalesAndLinesSettings?.showHighLowLine ?? false;
    const showHighLowLabel = scalesAndLinesSettings?.showHighLowLabel ?? false;

    const selectedValues = [
        ...(showHighLowLine ? ["showHighLowLine"] : []),
        ...(showHighLowLabel ? ["showHighLowLabel"] : []),
    ];

    const handleHighLowSelectChange = (event: SelectChangeEvent<string[]>) => {
        const values = event.target.value as string[];
        dispatch(updateChartConfig({
            viewKey,
            config: {
                scalesAndLinesSettings: {
                    ...scalesAndLinesSettings,
                    showHighLowLine: values.includes("showHighLowLine"),
                    showHighLowLabel: values.includes("showHighLowLabel"),
                }
            },
            createIfAbsent: false
        }));
    };

    const showIndicatorValue = scalesAndLinesSettings?.showIndicatorValue ?? true;
    const showIndicatorLabel = scalesAndLinesSettings?.showIndicatorLabel ?? true;

    const selectedIndicatorOptions = [
        ...(showIndicatorLabel ? ["showIndicatorLabel"] : []),
        ...(showIndicatorValue ? ["showIndicatorValue"] : []),
    ];

    const handleIndicatorSelectChange = (event: SelectChangeEvent<string[]>) => {
        const values = event.target.value as string[];
        dispatch(updateChartConfig({
            viewKey,
            config: {
                scalesAndLinesSettings: {
                    ...scalesAndLinesSettings,
                    showIndicatorValue: values.includes("showIndicatorValue"),
                    showIndicatorLabel: values.includes("showIndicatorLabel"),
                }
            },
            createIfAbsent: false
        }));
    };

    const showPrevDayCloseLine = scalesAndLinesSettings?.showPrevDayCloseLine ?? false;
    const showPrevDayCloseLabel = scalesAndLinesSettings?.showPrevDayCloseLabel ?? false;

    const selectedPrevDayCloseOptions = [
        ...(showPrevDayCloseLine ? ["showPrevDayCloseLine"] : []),
        ...(showPrevDayCloseLabel ? ["showPrevDayCloseLabel"] : []),
    ];

    const handlePrevDayCloseSelectChange = (event: SelectChangeEvent<string[]>) => {
        const values = event.target.value as string[];
        dispatch(updateChartConfig({
            viewKey,
            config: {
                scalesAndLinesSettings: {
                    ...scalesAndLinesSettings,
                    showPrevDayCloseLine: values.includes("showPrevDayCloseLine"),
                    showPrevDayCloseLabel: values.includes("showPrevDayCloseLabel"),
                }
            },
            createIfAbsent: false
        }));
    };

    const showSymbolLine = scalesAndLinesSettings?.showSymbolLine ?? true;
    const showSymbolValue = scalesAndLinesSettings?.showSymbolValue ?? true;

    const selectedSymbolOptions = [
        ...(showSymbolLine ? ["showSymbolLine"] : []),
        ...(showSymbolValue ? ["showSymbolValue"] : []),
    ];

    const handleSymbolSelectChange = (event: SelectChangeEvent<string[]>) => {
        const values = event.target.value as string[];
        dispatch(updateChartConfig({
            viewKey,
            config: {
                scalesAndLinesSettings: {
                    ...scalesAndLinesSettings,
                    showSymbolLine: values.includes("showSymbolLine"),
                    showSymbolValue: values.includes("showSymbolValue"),
                }
            },
            createIfAbsent: false
        }));
    };

    return (
        <Box>
            <Typography variant="subtitle1" sx={chartSettingsSectionTitleSx}>
                PRICE SCALE
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!(scalesAndLinesSettings?.showPlusButton ?? true)}
                            onChange={(e) => handleCheckboxChange(e, "showPlusButton")}
                        />
                    }
                    label="Plus button"
                    sx={checkboxSx}
                />
                {showCountdownSetting ? (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={!!(scalesAndLinesSettings?.showCountdown ?? true)}
                                onChange={(e) => handleCheckboxChange(e, "showCountdown")}
                            />
                        }
                        label="Countdown to bar close"
                        sx={checkboxSx}
                    />
                ) : null}
            </FormGroup>
            <Typography variant="subtitle1" sx={{ ...chartSettingsSectionTitleSx, ...chartSettingsSectionDividerSx }}>
                PRICE LABELS & LINES
            </Typography>
            <FormGroup sx={chartSettingsFormGroupSx}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!(scalesAndLinesSettings?.alignLabels ?? true)}
                            onChange={(e) => handleCheckboxChange(e, "alignLabels")}
                        />
                    }
                    label="No overlapping labels"
                    sx={checkboxSx}
                />
                <Box sx={{ display: "grid", gridTemplateColumns: "50% 50%", alignItems: "center", minHeight: 36 }}>
                    <Typography fontSize="14px">High and Low</Typography>
                    <Select
                        multiple
                        size="small"
                        displayEmpty
                        value={selectedValues}
                        onChange={handleHighLowSelectChange}
                        renderValue={(selected) => {
                            if (!(selected as string[]).length) return <Typography fontSize="13px" color="text.disabled">Hidden</Typography>;
                            return (
                                <Typography fontSize="13px">
                                    {(selected as string[]).map(v => HIGH_LOW_OPTIONS.find(o => o.value === v)?.label).join(", ")}
                                </Typography>
                            );
                        }}
                        sx={{ width: "100%", fontSize: "13px", height: 32 }}
                        MenuProps={{}}
                    >
                        {HIGH_LOW_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value} dense>
                                <Checkbox
                                    checked={selectedValues.includes(option.value)}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                />
                                <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: "13px" }} />
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "50% 50%", alignItems: "center", minHeight: 36 }}>
                    <Typography fontSize="14px">Prev. day close</Typography>
                    <Select
                        multiple
                        size="small"
                        displayEmpty
                        value={selectedPrevDayCloseOptions}
                        onChange={handlePrevDayCloseSelectChange}
                        renderValue={(selected) => {
                            if (!(selected as string[]).length) return <Typography fontSize="13px" color="text.disabled">Hidden</Typography>;
                            return (
                                <Typography fontSize="13px">
                                    {(selected as string[]).map(v => PREV_DAY_CLOSE_OPTIONS.find(o => o.value === v)?.label).join(", ")}
                                </Typography>
                            );
                        }}
                        sx={{ width: "100%", fontSize: "13px", height: 32 }}
                        MenuProps={{}}
                    >
                        {PREV_DAY_CLOSE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value} dense>
                                <Checkbox
                                    checked={selectedPrevDayCloseOptions.includes(option.value)}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                />
                                <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: "13px" }} />
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "50% 50%", alignItems: "center", minHeight: 36 }}>
                    <Typography fontSize="14px">Symbol</Typography>
                    <Select
                        multiple
                        size="small"
                        displayEmpty
                        value={selectedSymbolOptions}
                        onChange={handleSymbolSelectChange}
                        renderValue={(selected) => {
                            if (!(selected as string[]).length) return <Typography fontSize="13px" color="text.disabled">Hidden</Typography>;
                            return (
                                <Typography fontSize="13px">
                                    {(selected as string[]).map(v => SYMBOL_OPTIONS.find(o => o.value === v)?.label).join(", ")}
                                </Typography>
                            );
                        }}
                        sx={{ width: "100%", fontSize: "13px", height: 32 }}
                        MenuProps={{}}
                    >
                        {SYMBOL_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value} dense>
                                <Checkbox
                                    checked={selectedSymbolOptions.includes(option.value)}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                />
                                <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: "13px" }} />
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "50% 50%", alignItems: "center", minHeight: 36 }}>
                    <Typography fontSize="14px">Indicator values</Typography>
                    <Select
                        multiple
                        size="small"
                        displayEmpty
                        value={selectedIndicatorOptions}
                        onChange={handleIndicatorSelectChange}
                        renderValue={(selected) => {
                            if (!(selected as string[]).length) return <Typography fontSize="13px" color="text.disabled">Hidden</Typography>;
                            return (
                                <Typography fontSize="13px">
                                    {(selected as string[]).map(v => INDICATOR_OPTIONS.find(o => o.value === v)?.label).join(", ")}
                                </Typography>
                            );
                        }}
                        sx={{ width: "100%", fontSize: "13px", height: 32 }}
                        MenuProps={{}}
                    >
                        {INDICATOR_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value} dense>
                                <Checkbox
                                    checked={selectedIndicatorOptions.includes(option.value)}
                                    size="small"
                                    sx={{ p: 0.5 }}
                                />
                                <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: "13px" }} />
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </FormGroup>
        </Box>
    );
};

export default ScalesAndLinesSettings;
